import React from 'react';
import { WeaveTheme } from '@weave/theme-original';
import { css } from '@emotion/core';
import { Modal, PrimaryButton, CopyIcon, Text } from '@weave/design-system';

import { useFaxTestActions } from './use-fax-test-actions';
import { SendTestFaxForm } from './components/send-test-fax-form.component';
import { CheckFaxStatusSection } from './components/check-fax-status-section.component';
import { FaxListTable } from './components/fax-list-table.component';

interface FaxTestSectionProps {
  currentLocationId: string;
}

export const FaxTestSection: React.FC<FaxTestSectionProps> = ({ currentLocationId }) => {
  const {
    // Send Test Fax
    sendTestFaxLoading,
    resultingFaxId,
    showResultModal,
    setShowResultModal,
    createContactAndSendFax,

    // Check Fax Status
    faxStatus,
    checkStatusLoading,
    checkFaxStatus,

    // Last Sent Faxes
    lastSentFaxes,
    lastSentFaxesLoading,
    sentPageSize,
    setSentPageSize,
    fetchLastSentFaxes,

    // Last Received Faxes
    lastReceivedFaxes,
    lastReceivedFaxesLoading,
    receivedPageSize,
    setReceivedPageSize,
    fetchLastReceivedFaxes,

    // Utilities
    copyToClipboard,
  } = useFaxTestActions(currentLocationId);

  const [currentCheckFaxId, setCurrentCheckFaxId] = React.useState('');

  const handleFaxSent = (faxId: string) => {
    setCurrentCheckFaxId(faxId);
  };

  const handleFaxIdClick = (faxId: string) => {
    setCurrentCheckFaxId(faxId);
    copyToClipboard(faxId);
  };

  return (
    <>
      {/* Send Test Fax and Check Status Section */}
      <div
        css={(theme: WeaveTheme) => css`
          display: flex;
          gap: ${theme.spacing(2)};
          margin-top: ${theme.spacing(4)};
          margin-bottom: ${theme.spacing(4)};
          padding-top: ${theme.spacing(2)};
          border-top: 1px solid ${theme.colors.gray['300']};
        `}
      >
        <SendTestFaxForm
          onSendFax={createContactAndSendFax}
          isLoading={sendTestFaxLoading}
          onFaxSent={handleFaxSent}
        />

        <div css={css`margin-left: auto;`}>
          <CheckFaxStatusSection
            onCheckStatus={checkFaxStatus}
            isLoading={checkStatusLoading}
            faxStatus={faxStatus}
            initialFaxId={currentCheckFaxId}
          />
        </div>
      </div>

      {/* Last Sent Faxes Section */}
      <FaxListTable
        title={`Last ${sentPageSize} Sent Faxes`}
        faxes={lastSentFaxes}
        isLoading={lastSentFaxesLoading}
        pageSize={sentPageSize}
        onPageSizeChange={(newSize) => {
          setSentPageSize(newSize);
          fetchLastSentFaxes(newSize);
        }}
        onRefresh={() => fetchLastSentFaxes(sentPageSize)}
        onFaxIdClick={handleFaxIdClick}
        onCopyToClipboard={copyToClipboard}
        direction="sent"
      />

      {/* Last Received Faxes Section */}
      <FaxListTable
        title={`Last ${receivedPageSize} Received Faxes`}
        faxes={lastReceivedFaxes}
        isLoading={lastReceivedFaxesLoading}
        pageSize={receivedPageSize}
        onPageSizeChange={(newSize) => {
          setReceivedPageSize(newSize);
          fetchLastReceivedFaxes(newSize);
        }}
        onRefresh={() => fetchLastReceivedFaxes(receivedPageSize)}
        onFaxIdClick={handleFaxIdClick}
        onCopyToClipboard={copyToClipboard}
        direction="received"
      />

      {/* Result Fax ID Modal */}
      {resultingFaxId && (
        <Modal show={showResultModal} onClose={() => setShowResultModal(false)} maxWidth={500}>
          <Modal.Header>✅ Fax Sent Successfully!</Modal.Header>
          <Modal.Body>
            <Text
              css={(theme: WeaveTheme) => css`
                margin-bottom: ${theme.spacing(2)};
                color: ${theme.colors.gray['600']};
              `}
            >
              The fax has been forwarded. Here is the new Fax ID:
            </Text>
            <div
              css={(theme: WeaveTheme) => css`
                background-color: ${theme.colors.gray['100']};
                border: 1px solid ${theme.colors.gray['300']};
                border-radius: ${theme.borderRadius.small};
                padding: ${theme.spacing(1.5)};
                display: flex;
                align-items: center;
                gap: ${theme.spacing(1)};
                margin-bottom: ${theme.spacing(2)};
              `}
            >
              <code
                css={(theme: WeaveTheme) => css`
                  flex: 1;
                  font-size: ${theme.fontSize(14)};
                  word-wrap: break-word;
                  overflow-wrap: break-word;
                  white-space: normal;
                `}
              >
                {resultingFaxId}
              </code>
              <PrimaryButton size="small" onClick={() => copyToClipboard(resultingFaxId)}>
                <CopyIcon /> Copy
              </PrimaryButton>
            </div>
          </Modal.Body>
          <Modal.Actions
            secondaryLabel="Close"
            onSecondaryClick={() => setShowResultModal(false)}
            primaryLabel="Check Status"
            onPrimaryClick={() => {
              setShowResultModal(false);
              checkFaxStatus(resultingFaxId);
            }}
          />
        </Modal>
      )}
    </>
  );
};
